/**
 * Color Utilities
 * Functions for generating color variations and gradients from a primary color
 */

// The hex ↔ RGB primitives live in `@/lib/color/hex` (the shared, framework-agnostic home), and
// the WCAG luminance/contrast math beside them in `@/lib/color/contrast`. Both are imported for
// internal use and re-exported so existing landing-page importers keep their path.
import {
  contrastRatio,
  pickReadableOnBackground,
  relativeLuminance,
  WCAG_AA_NORMAL_TEXT_CONTRAST,
} from "@/lib/color/contrast";
import { hexToRgb, rgbToHex } from "@/lib/color/hex";

export { hexToRgb, pickReadableOnBackground, rgbToHex };

// Constants
const LIGHTEN_PERCENTAGE = 40;
const DARKEN_PERCENTAGE = 40;
const HOVER_DARKEN_PERCENTAGE = 10;
const LUMINANCE_THRESHOLD = 0.5;

/**
 * Validate if a value is a valid 6-digit hex color, with or without a leading `#`.
 *
 * Guards `typeof` explicitly — the `string` param type is only a compile-time promise; a value
 * that reaches this function from unvalidated external data (a server action's request body, a
 * copilot tool call) can violate it at runtime, and `RegExp.test` coerces its argument with
 * `String(...)` rather than rejecting a non-string — `["#1e3a8a"]` stringifies to `"#1e3a8a"` and
 * would otherwise pass as a valid color, only to crash the first caller (e.g. `toCssHex`) that
 * calls a string-only method on the real array.
 *
 * @param hex - The candidate color. Typed `string`, but treated as untrusted at runtime.
 * @returns True when `hex` is a string matching `#rrggbb` or `rrggbb`.
 */
export function isValidHexColor(hex: string): boolean {
  return typeof hex === "string" && /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.test(hex);
}

export type DetectedColor = {
  mode: "hex" | "rgb";
  /** Canonical display string for the input field. */
  display: string;
  /** Always-lowercase `#rrggbb` hex value to commit via onChange. */
  hex: string;
};

/**
 * Detect a pasted color string and return its canonical form, or null if unrecognized.
 * Recognizes 6-digit hex (with or without `#`) and comma-separated RGB triples.
 * @param input - Raw pasted text.
 * @returns Detected color with canonical display and hex, or null if not a recognized color.
 */
export function detectPastedColor(input: string): DetectedColor | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const hexMatch = trimmed.match(/^#?([0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = `#${hexMatch[1].toLowerCase()}`;
    return { mode: "hex", display: hex, hex };
  }

  const rgbMatch = trimmed.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    if ([r, g, b].some((n) => !Number.isFinite(n) || n < 0 || n > 255)) return null;
    return { mode: "rgb", display: `${r}, ${g}, ${b}`, hex: rgbToHex(r, g, b) };
  }

  return null;
}

/**
 * Lighten a color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const amount = Math.round(2.55 * percent);

  return rgbToHex(Math.min(255, r + amount), Math.min(255, g + amount), Math.min(255, b + amount));
}

/**
 * Darken a color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const amount = Math.round(2.55 * percent);

  return rgbToHex(Math.max(0, r - amount), Math.max(0, g - amount), Math.max(0, b - amount));
}

const READABLE_COLOR_DARKEN_STEP = 8;
const READABLE_COLOR_MAX_ITERATIONS = 20;

/**
 * Returns a darkened variant of `hex` that meets a minimum WCAG contrast ratio against `bgHex`.
 * Iteratively darkens the input until the threshold is met (or we hit black). Used so brand
 * accent colors render legible link text even when the brand color is a pastel.
 * @param hex - Source color (any valid hex).
 * @param bgHex - Background color to contrast against. Defaults to white.
 * @param minRatio - Minimum WCAG contrast ratio. Defaults to {@link WCAG_AA_NORMAL_TEXT_CONTRAST}.
 * @returns A hex color with sufficient contrast against `bgHex`, or the original if invalid.
 */
export function getReadableTextColor(
  hex: string,
  bgHex: string = "#ffffff",
  minRatio: number = WCAG_AA_NORMAL_TEXT_CONTRAST
): string {
  if (!isValidHexColor(hex)) return hex;
  let candidate = hex;
  for (let i = 0; i < READABLE_COLOR_MAX_ITERATIONS; i++) {
    if (contrastRatio(candidate, bgHex) >= minRatio) return candidate;
    candidate = darkenColor(candidate, READABLE_COLOR_DARKEN_STEP);
  }
  return candidate;
}

/**
 * Returns a lightened-or-darkened variant of `hex` that meets a minimum WCAG contrast ratio
 * against `bgHex`, moving whichever direction actually helps. Unlike {@link getReadableTextColor}
 * (which only darkens — correct when `bgHex` is known to be light, e.g. a button fill), this is
 * for accent/link colors sitting on a background whose lightness isn't known ahead of time (a
 * user-chosen section color, or a dark image scrim) — darkening a pastel accent on a dark
 * background would walk it toward black, the opposite of readable.
 *
 * The lighten-or-darken direction is picked by *measuring* which of pure white/black actually
 * contrasts better against `bgHex`, not by a luminance threshold — WCAG's contrast formula has a
 * `+0.05` offset on both sides of the ratio, so it isn't symmetric around luminance 0.5 the way a
 * naive "light bg → darken, dark bg → lighten" rule assumes. The true white/black crossover sits
 * near luminance 0.18: a mid-tone background above that (amber, green, many brand colors) still
 * has better contrast against black than white, so a 0.5 cutoff picked "lighten" for those and
 * walked the accent to a barely-there ~2:1 near-white instead of a legible dark one.
 * @param hex - Source color (any valid hex).
 * @param bgHex - Background color to contrast against. Defaults to white.
 * @param minRatio - Minimum WCAG contrast ratio. Defaults to {@link WCAG_AA_NORMAL_TEXT_CONTRAST}.
 * @returns A hex color with sufficient contrast against `bgHex`, or the original if invalid.
 */
export function getReadableAccentColor(
  hex: string,
  bgHex: string = "#ffffff",
  minRatio: number = WCAG_AA_NORMAL_TEXT_CONTRAST
): string {
  if (!isValidHexColor(hex)) return hex;
  if (contrastRatio(hex, bgHex) >= minRatio) return hex;
  const lighten = contrastRatio("#ffffff", bgHex) >= contrastRatio("#000000", bgHex);
  let candidate = hex;
  for (let i = 0; i < READABLE_COLOR_MAX_ITERATIONS; i++) {
    candidate = lighten
      ? lightenColor(candidate, READABLE_COLOR_DARKEN_STEP)
      : darkenColor(candidate, READABLE_COLOR_DARKEN_STEP);
    if (contrastRatio(candidate, bgHex) >= minRatio) return candidate;
  }
  return candidate;
}

// Backgrounds at or below this WCAG relative luminance keep white button text. Above it
// (genuine pastels), we flip to dark text. Brand mid-tones like orange (~0.28) stay white
// even though strict WCAG AA would push them dark — matches the chosen visual style.
const BUTTON_LIGHT_BG_LUMINANCE_THRESHOLD = 0.55;

/**
 * Picks white-or-dark text for a colored button using a luminance check, leaning toward white.
 * Use for primary/accent-colored CTA buttons where keeping white text on brand mid-tones
 * (orange, indigo, etc.) matters more than strict WCAG AA contrast.
 * @param bgHex - The button's background color.
 * @param lightOption - Text color for darker backgrounds. Defaults to white.
 * @param darkOption - Text color for light/pastel backgrounds. Defaults to slate-900.
 * @returns `lightOption` when `bgHex` is dark enough, else `darkOption`.
 */
export function getButtonTextColor(
  bgHex: string,
  lightOption: string = "#ffffff",
  darkOption: string = "#0f172a"
): string {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return lightOption;
  return relativeLuminance(rgb) > BUTTON_LIGHT_BG_LUMINANCE_THRESHOLD ? darkOption : lightOption;
}

/**
 * Generate a color palette from a primary color
 */
export interface ColorPalette {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  gradient: {
    from: string;
    to: string;
  };
  text: string; // Best text color (white or black) for the primary color
  buttonHover: string;
}

export function generateColorPalette(primaryColor: string): ColorPalette {
  // Validate input and fallback to default if invalid
  const validColor = isValidHexColor(primaryColor) ? primaryColor : DEFAULT_PRIMARY_COLOR;

  const lighterColor = lightenColor(validColor, LIGHTEN_PERCENTAGE);
  const darkerColor = darkenColor(validColor, DARKEN_PERCENTAGE);
  const hoverColor = darkenColor(validColor, HOVER_DARKEN_PERCENTAGE);

  // Determine best text color based on luminance
  const rgb = hexToRgb(validColor);
  let textColor = "#ffffff";
  if (rgb) {
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    textColor = luminance > LUMINANCE_THRESHOLD ? "#000000" : "#ffffff";
  }

  return {
    primary: validColor,
    primaryLight: lighterColor,
    primaryDark: darkerColor,
    gradient: {
      from: lighterColor,
      to: darkerColor,
    },
    text: textColor,
    buttonHover: hoverColor,
  };
}

/**
 * Generate gradient style from a color palette
 */
export function getGradientStyle(palette: ColorPalette): React.CSSProperties {
  return {
    background: `linear-gradient(to bottom right, ${palette.gradient.from}, ${palette.gradient.to})`,
  };
}

/**
 * Default primary color (indigo)
 */
export const DEFAULT_PRIMARY_COLOR = "#5261EB";
